import React, { useState } from 'react';
import GrapesJSEditor from '../components/GrapesJSEditor/GrapesJSEditor';
import SimpleTest from '../components/GrapesJSEditor/SimpleTest';
import LocalTest from '../components/GrapesJSEditor/LocalTest';
import IframeEditor from '../components/GrapesJSEditor/IframeEditor';
import ProxyEditor from '../components/GrapesJSEditor/ProxyEditor';
import ProxyIframeEditor from '../components/GrapesJSEditor/ProxyIframeEditor';
import EnhancedIframeEditor from '../components/GrapesJSEditor/EnhancedIframeEditor';

const TestPageEditor: React.FC = () => {
  const [url, setUrl] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedHtml, setCapturedHtml] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [error, setError] = useState('');
  const [showSimpleTest, setShowSimpleTest] = useState(false);
  const [showLocalTest, setShowLocalTest] = useState(false);
  const [showIframeEditor, setShowIframeEditor] = useState(false);
  const [showProxyEditor, setShowProxyEditor] = useState(false);
  const [showProxyIframeEditor, setShowProxyIframeEditor] = useState(false);
  const [showEnhancedIframeEditor, setShowEnhancedIframeEditor] = useState(false);
  const [editorMode, setEditorMode] = useState<'grapesjs' | 'iframe'>('grapesjs');

  // Sample HTML content for immediate testing
  const sampleHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sample Test Page - GrapesJS Editor</title>
    <style>
        body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
            margin: 0; 
            padding: 0; 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
        }
        .container { 
            max-width: 1200px; 
            margin: 0 auto; 
            background: white; 
            min-height: 100vh;
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
        }
        .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 60px 40px;
            text-align: center;
        }
        .header h1 {
            font-size: 3rem;
            margin-bottom: 20px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }
        .header p {
            font-size: 1.2rem;
            opacity: 0.9;
        }
        .content { 
            padding: 40px;
            line-height: 1.8;
        }
        .section {
            margin-bottom: 40px;
            padding: 30px;
            border-radius: 10px;
            background: #f8f9fa;
            border-left: 5px solid #667eea;
        }
        .button { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white; 
            padding: 15px 30px; 
            border: none; 
            border-radius: 25px; 
            cursor: pointer; 
            font-size: 1rem;
            font-weight: 600;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }
        .button:hover { 
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
        }
        .info-box { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 25px; 
            border-radius: 10px; 
            margin: 30px 0;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }
        .feature-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 30px;
            margin: 40px 0;
        }
        .feature-card {
            background: white;
            padding: 30px;
            border-radius: 15px;
            box-shadow: 0 5px 20px rgba(0,0,0,0.1);
            text-align: center;
            transition: transform 0.3s ease;
        }
        .feature-card:hover {
            transform: translateY(-5px);
        }
        .feature-icon {
            font-size: 3rem;
            color: #667eea;
            margin-bottom: 20px;
        }
        blockquote { 
            border-left: 5px solid #667eea; 
            padding: 20px; 
            margin: 30px 0; 
            font-style: italic;
            background: #f8f9fa;
            border-radius: 0 10px 10px 0;
        }
        .cta-section {
            text-align: center;
            padding: 60px 40px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            margin-top: 40px;
        }
        .stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 30px;
            margin: 40px 0;
        }
        .stat-item {
            text-align: center;
            padding: 20px;
        }
        .stat-number {
            font-size: 2.5rem;
            font-weight: bold;
            color: #667eea;
        }
        .stat-label {
            color: #666;
            font-size: 1.1rem;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🎨 GrapesJS Visual Editor</h1>
            <p>Professional drag-and-drop page builder with real-time editing</p>
        </div>
        
        <div class="content">
            <div class="section">
                <h2>🚀 Welcome to the Visual Editor</h2>
                <p>This is a sample page demonstrating the powerful GrapesJS visual editor. You can edit any element on this page by clicking on it and using the properties panel on the right.</p>
                
                <div class="info-box">
                    <h3>✨ Key Features Available:</h3>
                    <ul style="margin: 0; padding-left: 20px;">
                        <li><strong>Drag & Drop:</strong> Add components from the left panel</li>
                        <li><strong>Visual Editing:</strong> Click any element to edit it</li>
                        <li><strong>Real-time Preview:</strong> See changes instantly</li>
                        <li><strong>Style Manager:</strong> Customize colors, fonts, spacing</li>
                        <li><strong>Component Library:</strong> Pre-built blocks and elements</li>
                        <li><strong>Responsive Design:</strong> Test on desktop, tablet, mobile</li>
                    </ul>
                </div>
            </div>

            <div class="feature-grid">
                <div class="feature-card">
                    <div class="feature-icon">🎯</div>
                    <h3>Easy to Use</h3>
                    <p>Intuitive drag-and-drop interface that makes page building simple and enjoyable.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">⚡</div>
                    <h3>Fast Performance</h3>
                    <p>Lightning-fast editing with real-time preview and instant updates.</p>
                </div>
                <div class="feature-card">
                    <div class="feature-icon">🎨</div>
                    <h3>Beautiful Design</h3>
                    <p>Create stunning pages with our comprehensive design tools and components.</p>
                </div>
            </div>

            <div class="section">
                <h2>📊 Sample Statistics</h2>
                <div class="stats">
                    <div class="stat-item">
                        <div class="stat-number">100+</div>
                        <div class="stat-label">Components</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">50+</div>
                        <div class="stat-label">Templates</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">24/7</div>
                        <div class="stat-label">Support</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">99.9%</div>
                        <div class="stat-label">Uptime</div>
                    </div>
                </div>
            </div>

            <div class="section">
                <h2>💡 Getting Started</h2>
                <p>To start editing this page:</p>
                <ol>
                    <li><strong>Click any element</strong> on this page to select it</li>
                    <li><strong>Use the properties panel</strong> on the right to modify styles</li>
                    <li><strong>Drag components</strong> from the left panel to add new elements</li>
                    <li><strong>Switch devices</strong> using the top toolbar to test responsiveness</li>
                    <li><strong>Save your changes</strong> when you're satisfied with the result</li>
                </ol>
                
                <button class="button">🎯 Try Editing This Button!</button>
            </div>

            <blockquote>
                "The best way to learn is by doing. Start editing this page right now and discover the power of visual page building!"
            </blockquote>

            <div class="cta-section">
                <h2>🎉 Ready to Create Amazing Pages?</h2>
                <p>Start building your next masterpiece with our powerful visual editor!</p>
                <button class="button" style="background: white; color: #667eea; margin-top: 20px;">
                    🚀 Start Building Now
                </button>
            </div>
        </div>
    </div>
</body>
</html>`;

  const handleLoadSampleContent = () => {
    setCapturedHtml(sampleHtml);
    setShowEditor(true);
  };

  const handleCapture = async () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    setIsCapturing(true);
    setError('');

    try {
      // Use a CORS proxy to fetch the HTML directly
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url.trim())}`;
      
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.contents) {
        // Clean and process the captured content
        let cleanedContent = data.contents
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
          .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '') // Remove style tags
          .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
          .replace(/on\w+='[^']*'/gi, '') // Remove event handlers
          .replace(/javascript:/gi, '') // Remove javascript: protocols
          .replace(/data-src/gi, 'src') // Convert data-src to src for images
          .replace(/srcset="[^"]*"/gi, '') // Remove srcset attributes
          .replace(/sizes="[^"]*"/gi, '') // Remove sizes attributes
          .replace(/<!--[\s\S]*?-->/g, ''); // Remove HTML comments
        
        // Create a basic HTML structure with the captured content
        const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Captured Page - ${url}</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            line-height: 1.6;
            color: #333;
        }
        .captured-content { 
            border: 1px solid #ddd; 
            padding: 20px; 
            margin: 20px 0; 
            background: white;
            border-radius: 8px;
        }
        .info { 
            background: #f8f9fa; 
            padding: 15px; 
            border-radius: 8px; 
            margin-bottom: 20px; 
            border-left: 4px solid #007bff;
        }
        .info h2 {
            margin-top: 0;
            color: #007bff;
        }
        img {
            max-width: 100%;
            height: auto;
        }
        a {
            color: #007bff;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
    </style>
</head>
<body>
    <div class="info">
        <h2>📄 Captured Page Content</h2>
        <p><strong>URL:</strong> <a href="${url}" target="_blank">${url}</a></p>
        <p><strong>Captured at:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Content length:</strong> ${cleanedContent.length} characters</p>
    </div>
    <div class="captured-content">
        ${cleanedContent}
    </div>
</body>
</html>`;
        
        setCapturedHtml(html);
        setShowEditor(true);
      } else {
        throw new Error('Failed to capture page content');
      }
    } catch (err) {
      console.error('Error capturing page:', err);
      setError(`Failed to capture page: ${err instanceof Error ? err.message : 'Unknown error'}`);
      
      // Fallback: Use sample content
      setCapturedHtml(sampleHtml);
      setShowEditor(true);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleSave = (content: string) => {
    console.log('Saving content:', content);
    alert('Content saved! Check console for details.');
  };

  const handleClose = () => {
    setShowEditor(false);
    setShowIframeEditor(false);
    setShowProxyEditor(false);
    setShowProxyIframeEditor(false);
    setShowEnhancedIframeEditor(false);
    setCapturedHtml('');
  };

  const handleIframeEdit = () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }
    setShowIframeEditor(true);
  };

  const handleProxyEdit = () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }
    setShowProxyEditor(true);
  };

  const handleProxyIframeEdit = () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }
    setShowProxyIframeEditor(true);
  };

  const handleEnhancedIframeEdit = () => {
    if (!url.trim()) {
      setError('Please enter a valid URL');
      return;
    }
    setShowEnhancedIframeEditor(true);
  };

  if (showEnhancedIframeEditor) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <EnhancedIframeEditor
          url={url}
          onSave={handleSave}
          onClose={handleClose}
        />
      </div>
    );
  }

  if (showProxyIframeEditor) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <ProxyIframeEditor
          url={url}
          onSave={handleSave}
          onClose={handleClose}
        />
      </div>
    );
  }

  if (showProxyEditor) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <ProxyEditor
          url={url}
          onSave={handleSave}
          onClose={handleClose}
        />
      </div>
    );
  }

  if (showIframeEditor) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <IframeEditor
          url={url}
          onSave={handleSave}
          onClose={handleClose}
        />
      </div>
    );
  }

  if (showLocalTest) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="p-3 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Local GrapesJS React Test</h5>
            <div>
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setShowLocalTest(false)}>
                <i className="fas fa-arrow-left me-1"></i>
                Back to Options
              </button>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <LocalTest />
        </div>
      </div>
    );
  }

  if (showSimpleTest) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="p-3 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">GrapesJS Simple Test</h5>
            <div>
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setShowSimpleTest(false)}>
                <i className="fas fa-arrow-left me-1"></i>
                Back to Options
              </button>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <SimpleTest />
        </div>
      </div>
    );
  }

  if (showEditor) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="p-3 border-bottom bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">GrapesJS Visual Editor</h5>
            <div>
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={handleClose}>
                <i className="fas fa-arrow-left me-1"></i>
                Back to Options
              </button>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <GrapesJSEditor
            initialContent={capturedHtml}
            onSave={handleSave}
            onClose={handleClose}
            baseUrl={url}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="col-md-8 col-lg-6">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                <i className="fas fa-edit me-2"></i>
                Enhanced Website Editor
              </h4>
            </div>
            <div className="card-body p-4">
              
              {/* Enhanced Iframe Editor Section */}
              <div className="mb-4">
                <h5 className="text-primary mb-3">
                  <i className="fas fa-rocket me-2"></i>
                  Enhanced Iframe Editor
                </h5>
                <div className="d-grid mb-4">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleEnhancedIframeEdit}
                    disabled={!url.trim()}
                  >
                    <i className="fas fa-edit me-2"></i>
                    🚀 Enhanced Iframe (Blob URL - No Restrictions)
                  </button>
                </div>
                <div className="alert alert-primary" role="alert">
                  <i className="fas fa-star me-2"></i>
                  <strong>Enhanced Iframe Editor:</strong> Uses blob URLs to avoid iframe restrictions. 
                  Best option for editing websites with original CSS and full editing capabilities.
                </div>
              </div>

              <hr className="my-4" />

              {/* URL Input Section */}
              <div className="mb-4">
                <h5 className="text-dark mb-3">
                  <i className="fas fa-globe me-2"></i>
                  Enter Website URL
                </h5>
                <div className="mb-3">
                  <label htmlFor="url" className="form-label">
                    Website URL to Edit
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-globe"></i>
                    </span>
                    <input
                      type="url"
                      className="form-control"
                      id="url"
                      placeholder="https://example.com"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEnhancedIframeEdit()}
                    />
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
              </div>

              <div className="mt-3">
                <h6 className="text-muted">
                  <i className="fas fa-link me-2"></i>
                  Quick Test URLs:
                </h6>
                <div className="list-group list-group-flush">
                  <button
                    className="list-group-item list-group-item-action"
                    onClick={() => setUrl('https://example.com')}
                  >
                    https://example.com
                  </button>
                  <button
                    className="list-group-item list-group-item-action"
                    onClick={() => setUrl('https://httpbin.org/html')}
                  >
                    https://httpbin.org/html
                  </button>
                  <button
                    className="list-group-item list-group-item-action"
                    onClick={() => setUrl('https://jsonplaceholder.typicode.com')}
                  >
                    https://jsonplaceholder.typicode.com
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPageEditor;
